import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Bell,
  CreditCard,
  Shield,
  Gift,
  Check,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const notificationIcons = {
  transaction: CreditCard,
  security: Shield,
  promotion: Gift,
  default: Bell
};

const notificationColors = {
  transaction: "bg-blue-500/20 text-blue-400",
  security: "bg-red-500/20 text-red-400",
  promotion: "bg-purple-500/20 text-purple-400",
  default: "bg-white/10 text-muted-foreground"
};

const Notifications = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch notifications
  const { data: notifications, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const response = await axios.get(`${API}/notifications`);
      return response.data;
    }
  });

  // Mark all read mutation
  const markAllRead = useMutation({
    mutationFn: async () => {
      await axios.post(`${API}/notifications/mark-all-read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      queryClient.invalidateQueries(["notificationCount"]);
      toast.success("All notifications marked as read");
    }
  });

  // Mark single read mutation
  const markRead = useMutation({
    mutationFn: async (notificationId) => {
      await axios.post(`${API}/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      queryClient.invalidateQueries(["notificationCount"]);
    }
  });

  // Delete notification mutation
  const deleteNotification = useMutation({
    mutationFn: async (notificationId) => {
      await axios.delete(`${API}/notifications/${notificationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      queryClient.invalidateQueries(["notificationCount"]);
      toast.success("Notification deleted");
    }
  });

  const unreadCount = notifications?.filter(n => !n.read).length || 0;

  return (
    <div className="min-h-screen bg-background" data-testid="notifications-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center justify-between max-w-lg mx-auto">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-semibold text-xl text-white">Notifications</h1>
        </div>
        
        {unreadCount > 0 && (
          <button
            onClick={() => markAllRead.mutate()}
            className="text-sm font-medium text-pink-400 hover:opacity-80"
            data-testid="mark-all-read-btn"
          >
            Mark all read
          </button>
        )}
      </header>

      <main className="px-5 pb-24 max-w-lg mx-auto">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="glass rounded-2xl p-4 animate-pulse">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/5" />
                  <div className="flex-1">
                    <div className="h-4 bg-white/5 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-white/5 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : notifications?.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="font-heading font-semibold text-lg text-white mb-1">
              All caught up!
            </h2>
            <p className="text-muted-foreground text-sm">
              No notifications at the moment
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications?.map((notification) => {
              const IconComponent = notificationIcons[notification.type] || notificationIcons.default;
              const colorClass = notificationColors[notification.type] || notificationColors.default;
              
              return (
                <div
                  key={notification.notification_id}
                  className={`glass rounded-2xl p-4 transition-all ${!notification.read ? "border-pink-500/30 bg-pink-500/5" : ""}`}
                  data-testid={`notification-${notification.notification_id}`}
                >
                  <div className="flex gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white line-clamp-2">
                            {notification.title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {notification.message}
                          </p>
                          <p className="text-xs text-muted-foreground/50 mt-1">
                            {new Date(notification.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="w-2 h-2 rounded-full bg-pink-500 flex-shrink-0 mt-1" />
                        )}
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        {!notification.read && (
                          <button
                            onClick={() => markRead.mutate(notification.notification_id)}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-pink-400 transition-colors"
                            data-testid={`mark-read-${notification.notification_id}`}
                          >
                            <Check className="w-3 h-3" />
                            Mark read
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification.mutate(notification.notification_id)}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-400 transition-colors"
                          data-testid={`delete-${notification.notification_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Notifications;
